import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import { EntityTypesSupportedByMapsPage, MapEntity } from "@mapmycustomers/shared/types/entity";

import {
  locationPaginationTextGetterFactory,
  pinsCountTextGetterFactory,
  pinsPaginationTextGetterFactory,
  recordsCountTextGetterFactory,
  TextGetters,
} from "@app/component/RecordList";
import useMapView from "@app/component/view/utils/useMapView";
import CustomizeMenu from "@app/scene/map/components/CustomizeMenu";
import SelectedPinsSidebar from "@app/scene/map/components/SelectedPinsSidebar";
import MultiPinBackButton from "@app/scene/map/components/sidebar/MultiPinBackButton";
import MultiPinRecordList from "@app/scene/map/components/sidebar/MultiPinRecordList";
import { RecordList } from "@app/scene/map/components/sidebar/RecordList";
import BaseSidebar from "@app/scene/map/components/sidebar/Sidebar";
import { exitLassoMode } from "@app/scene/map/store/actions";
import {
  fetchRecords,
  hideMultiPin,
  hideSidebar,
  subscribeEmail,
  unsubscribeEmail,
} from "@app/scene/map/store/mapMode/actions";
import {
  getMultiPin,
  getRecords,
  getRecordsListRange,
  getRecordsListTotal,
  isRecordsListLoading,
  isSidebarVisible,
} from "@app/scene/map/store/mapMode/selectors";
import { isLassoLoading } from "@app/scene/map/store/selectors";
import { MAP_RECORD_LIST_SCROLL_KEY } from "@app/scene/map/utils/consts";
import { getEntityViewEntityType } from "@app/store/entityView";
import { RootState } from "@app/store/rootReducer";
import useToggleBooleanAfterTimeout from "@app/util/hook/useToggleBooleanAfterTimeout";
import { MAP_ENTITY_TYPES } from "@app/util/map/consts";
import { mapEntityIdGetter } from "@app/util/map/idGetters";

import EntityItem from "./components/EntityItem";
import EntityRecordView from "./components/EntityRecordView";
import MapModeNoData from "./components/MapModeNoData";
import Title from "./components/Title";

const messages = defineMessages({
  backToLocation: {
    id: "map.recordsList.multiPin.backToLocation",
    defaultMessage: "Back to Location",
    description: "Records list for multi-pin - Back to Location",
  },
  placeholder: {
    id: "map.recordsList.search.placeholder",
    defaultMessage: "Search records in view",
    description: "Records list - search placeholder",
  },
});

type SidebarProps = {
  onDataViewClick: () => void;
} & ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

const Sidebar: React.FC<SidebarProps> = ({
  isEntityViewVisible,
  isVisible,
  lassoLoading,
  loading,
  multiPin,
  onClose,
  onDataViewClick,
  onEmailSubscribe,
  onEmailUnsubscribe,
  onExitLassoMode,
  onFetchRecords,
  onHideMultiPin,
  range,
  records,
  recordsCount,
}) => {
  const intl = useIntl();
  const {
    viewState: { tool, visibleEntities },
  } = useMapView();

  const isLassoMode = tool === MapTool.LASSO;

  const isMultiPinMode = !!multiPin;
  const previousMultiPinModeRef = useRef(isMultiPinMode);

  const textGetters: TextGetters = useMemo(
    () => ({
      countTextGetter: isMultiPinMode
        ? recordsCountTextGetterFactory(intl)
        : pinsCountTextGetterFactory(intl, visibleEntities),
      paginationTextGetter: isMultiPinMode
        ? locationPaginationTextGetterFactory(intl)
        : pinsPaginationTextGetterFactory(intl),
    }),
    [intl, isMultiPinMode, visibleEntities]
  );

  useEffect(() => {
    // Refresh records list once exiting multi-pin mode
    if (isVisible && previousMultiPinModeRef.current && !multiPin) {
      onFetchRecords({ request: {} });
    }
    previousMultiPinModeRef.current = !!multiPin;
  }, [onFetchRecords, isMultiPinMode, multiPin, isVisible]);

  const handleRangeChange = useCallback(
    (range) => onFetchRecords({ request: { range } }),
    [onFetchRecords]
  );

  const handleSearch = useCallback(
    (search) => onFetchRecords({ request: { search } }),
    [onFetchRecords]
  );

  const handleClose = useCallback(() => {
    if (isMultiPinMode) {
      onHideMultiPin();
    } else if (isLassoMode) {
      onExitLassoMode();
    } else {
      onClose?.();
    }
  }, [onExitLassoMode, onHideMultiPin, isLassoMode, isMultiPinMode, onClose]);

  // When component is first-shown, we force to show loading skeleton
  const [firstLoading, setFirstLoading] = useState(true);
  useEffect(() => {
    if (isVisible && firstLoading && !loading) {
      setFirstLoading(false);
    } else if (!isVisible) {
      setFirstLoading(true);
    }
  }, [firstLoading, isVisible, loading]);

  const showLoading =
    useToggleBooleanAfterTimeout(1000, false, loading, true) || firstLoading || lassoLoading;

  if (!isVisible) {
    return null;
  }

  if (isLassoMode) {
    return (
      <SelectedPinsSidebar
        itemComponent={EntityItem}
        loading={loading}
        onDataViewClick={onDataViewClick}
        onEmailSubscribe={onEmailSubscribe}
        onEmailUnsubscribe={onEmailUnsubscribe}
        onFetchRecords={onFetchRecords}
        pinGroupsAndRoutesByDefault
        range={range}
        records={records}
        recordsCount={recordsCount}
        showDataView
      />
    );
  }

  return (
    <BaseSidebar
      beforeTitle={<MultiPinBackButton />}
      menu={<CustomizeMenu downloadDisabled={records.length === 0} />}
      onClose={handleClose}
      title={<Title />}
      visible={isVisible}
      wrapper={isEntityViewVisible}
    >
      <EntityRecordView
        backButtonLabel={isMultiPinMode ? intl.formatMessage(messages.backToLocation) : undefined}
        onClose={onClose}
      />
      {!isEntityViewVisible && (
        <>
          {isMultiPinMode ? (
            <MultiPinRecordList itemComponent={EntityItem} />
          ) : (
            <RecordList<MapEntity>
              idGetter={mapEntityIdGetter}
              itemComponent={EntityItem}
              loading={showLoading}
              noData={<MapModeNoData />}
              onEmailSubscribe={onEmailSubscribe}
              onEmailUnsubscribe={onEmailUnsubscribe}
              onRangeChange={handleRangeChange}
              onSearch={handleSearch}
              range={range}
              records={records}
              scrollPositionKey={MAP_RECORD_LIST_SCROLL_KEY}
              searchPlaceholder={intl.formatMessage(messages.placeholder)}
              showCount={false}
              textGetters={textGetters}
              total={recordsCount}
            />
          )}
        </>
      )}
    </BaseSidebar>
  );
};

const mapStateToProps = (state: RootState) => ({
  isEntityViewVisible: MAP_ENTITY_TYPES.includes(
    getEntityViewEntityType(state) as EntityTypesSupportedByMapsPage
  ),
  isVisible: isSidebarVisible(state),
  lassoLoading: isLassoLoading(state),
  loading: isRecordsListLoading(state),
  multiPin: getMultiPin(state),
  range: getRecordsListRange(state),
  records: getRecords(state),
  recordsCount: getRecordsListTotal(state),
});

const mapDispatchToProps = {
  onClose: hideSidebar,
  onEmailSubscribe: subscribeEmail.request,
  onEmailUnsubscribe: unsubscribeEmail.request,
  onExitLassoMode: exitLassoMode,
  onFetchRecords: fetchRecords.request,
  onHideMultiPin: hideMultiPin,
};

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);
